import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Stack, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { surface, brand, ink, line } from '../../theme/tokens';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => {
  const d = new Date(`${s}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
};

const monthGrid = (year, month) => {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, i) => new Date(Date.UTC(year, month, i + 1))),
  ];
};

/**
 * Pick arrival and departure, with nights that are already taken disabled.
 *
 * Two `<input type="date">` fields could only enforce min/max, so a guest could
 * pick dates that were gone and only find out after answering the meal
 * questions. Built rather than reached for @mui/x-date-pickers because that
 * plus date-fns is ~680 KB, and it had just been moved out of the guest bundle.
 *
 * A night is bookable but a departure day is not a night you occupy, so the
 * departure can land on a taken date -- you leave the morning someone else
 * arrives. Only the nights between arrival and departure must be free.
 *
 * `minDate`/`maxDate` are the booking window from Ananya's policy: three days'
 * notice and three months ahead. They gate ARRIVAL only -- a stay that starts
 * inside the window may of course run past the horizon.
 *
 * `singleDay` switches it to the day-visit product: one tap picks the whole
 * thing, so arrival and departure are the same date. A taken date is NOT
 * offered here even though it would be a legal departure for a stay -- one
 * booking takes the entire farm, so a day visit cannot share a night that
 * someone else already has.
 */
const StayDatePicker = ({
  startDate, endDate, takenNights = [], minDate = null, maxDate = null, onChange,
  today = new Date(), singleDay = false,
}) => {
  const taken = useMemo(() => new Set(takenNights), [takenNights]);
  const todayIso = iso(new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())));
  const [cursor, setCursor] = useState(() => {
    // `today` and not a fresh `new Date()`: the component already takes today as
    // a prop for the past-date check, and two notions of now inside one
    // component is how the grid ends up on a different month from the gating.
    const base = startDate ? new Date(`${startDate}T00:00:00Z`) : today;
    return new Date(Date.UTC(base.getUTCFullYear ? base.getUTCFullYear() : base.getFullYear(),
      base.getUTCMonth ? base.getUTCMonth() : base.getMonth(), 1));
  });

  const shift = (n) => setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + n, 1)));

  /** Nights occupied by a stay: arrival inclusive, departure exclusive. */
  const nightsBetween = (a, b) => {
    const out = [];
    let cur = a;
    while (cur < b) { out.push(cur); cur = addDays(cur, 1); }
    return out;
  };

  const rangeHasTaken = (a, b) => nightsBetween(a, b).some((d) => taken.has(d));

  /** Outside the booking window, and not usable as a departure either. */
  const outsideWindow = (key) => (minDate && key < minDate) || (maxDate && key > maxDate);

  const handleClick = (key) => {
    // a day visit is one date, so one tap is the whole answer -- asking for a
    // departure would mean tapping the same cell twice, which the "tap again
    // to start over" branch below reads as starting over instead.
    if (singleDay) { onChange({ startDate: key, endDate: key }); return; }
    // starting fresh, or restarting because a range is already set
    if (!startDate || (startDate && endDate)) {
      onChange({ startDate: key, endDate: '' });
      return;
    }
    if (key <= startDate) { onChange({ startDate: key, endDate: '' }); return; }
    if (rangeHasTaken(startDate, key)) {
      // the span crosses a night someone else has: treat it as a new arrival
      onChange({ startDate: key, endDate: '' });
      return;
    }
    onChange({ startDate, endDate: key });
  };

  const cells = monthGrid(cursor.getUTCFullYear(), cursor.getUTCMonth());
  const selectedNights = startDate && endDate ? nightsBetween(startDate, endDate) : [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <IconButton size="small" onClick={() => shift(-1)} aria-label="Previous month">
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: ink.primary }}>
          {MONTHS[cursor.getUTCMonth()]} {cursor.getUTCFullYear()}
        </Typography>
        <IconButton size="small" onClick={() => shift(1)} aria-label="Next month">
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {WEEKDAYS.map((w) => (
          <Typography key={w} variant="caption"
            sx={{ textAlign: 'center', color: ink.muted, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', pb: 0.5 }}>
            {w.toUpperCase()}
          </Typography>
        ))}

        {cells.map((date, i) => {
          if (!date) return <Box key={`b${i}`} />;
          const key = iso(date);
          const isPast = key < todayIso;
          const isTaken = taken.has(key);
          // A departure may fall past the horizon: you can book three months
          // out and stay a fortnight. Only the arrival is gated.
          const pickingArrival = singleDay || !startDate || Boolean(endDate);
          const outOfWindow = pickingArrival ? outsideWindow(key) : (minDate && key < minDate);
          const isArrival = key === startDate;
          const isDeparture = key === endDate;
          const inRange = selectedNights.includes(key) && !isArrival;
          // a taken night can still be a departure day -- you leave that
          // morning -- but a day visit occupies the whole day, so there it is
          // simply unavailable.
          const disabled = isPast || Boolean(outOfWindow)
            || (isTaken && !(!singleDay && startDate && !endDate && key > startDate));
          const selected = isArrival || isDeparture || inRange;

          return (
            <Box
              key={key}
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => handleClick(key)}
              aria-label={`${key}${isTaken ? ' (not available)' : outOfWindow ? ' (outside the booking window)' : ''}`}
              sx={{
                aspectRatio: '1 / 1', p: 0, font: 'inherit', fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                border: '1px solid', borderRadius: 1.5,
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderColor: selected ? 'transparent' : line.subtle,
                bgcolor: isArrival || isDeparture ? brand.brown
                  : inRange ? 'rgba(75,58,55,0.10)'
                    : isTaken ? surface.sunken : surface.card,
                color: isArrival || isDeparture ? ink.onDark
                  : isTaken || isPast ? ink.muted : ink.primary,
                textDecoration: isTaken ? 'line-through' : 'none',
                opacity: (isPast || outOfWindow) && !isTaken ? 0.45 : 1,
                '&:hover': { borderColor: disabled ? line.subtle : brand.mustardInk },
                '&:focus-visible': { outline: `2px solid ${brand.mustardInk}`, outlineOffset: 2 },
              }}
            >
              {date.getUTCDate()}
            </Box>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={singleDay ? 'Your day' : 'Your stay'}
          sx={{ bgcolor: brand.brown, color: ink.onDark, fontWeight: 600 }} />
        <Chip size="small" label="Already booked"
          sx={{ bgcolor: surface.sunken, color: ink.muted, textDecoration: 'line-through', fontWeight: 600 }} />
        <Typography variant="caption" sx={{ color: ink.secondary, alignSelf: 'center' }}>
          {singleDay
            ? (!startDate ? 'Tap the day you would like to come' : 'Tap another date to change it')
            : !startDate ? 'Tap your arrival date'
              : !endDate ? 'Now tap the day you leave'
                : 'Tap any date to start again'}
        </Typography>
      </Stack>
    </Box>
  );
};

export default StayDatePicker;
